import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, icons } from '@ublend-npm/aulaui-next';
import { Transforms } from 'slate';
import { ReactEditor, useSlateStatic, useReadOnly } from 'slate-react';
import { useAnalytics } from '../../lib/useAnalytics';
import { useAulaEditorContext } from '../../EditorContext';
import { LtiEntity, LtiLaunchType, LtiRequestType } from '../../Entities';
import { BlockToolbar, BlockToolbarItem } from '../BlockToolbar/BlockToolbar';
import {
  LtiBlockLinkWrapper,
  ButtonWrapper,
  TitleWrapper,
  ErrorWrapper,
  ErrorContainer,
  ErrorTitle,
  RetryContainer,
  Loader,
  ImageWrapper,
} from './Lti1p3Block.styled';

import { BlockElement } from '../../types';
import { Loading } from '../Loading/Loading';

import { getCurrentNode } from '../../lib/queries';
import { removeVoidElement } from '../../lib/commands';
import { DeepLinkingModal } from './DeepLinkingModal';
import { Lti1p3Form } from './Lti1p3Form';
import { lti1p3Launch } from './lti1p3Launch';

export type Lti1p3BlockProps = {
  element: BlockElement;
  title: string;
  icon: string;
  resourceLinkId: string;
  targetUrl: string;
  loginUrl: string;
  clientId: string;
  iss: string;
};

export const Lti1p3Block = ({
  title,
  icon,
  loginUrl,
  targetUrl,
  clientId,
  iss,
  element,
  resourceLinkId,
}: Lti1p3BlockProps) => {
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [loginHint, setLoginHint] = useState('');
  const editor = useSlateStatic();
  const isReadOnly = useReadOnly();
  const formRef = useRef<HTMLFormElement>(null);
  const track = useAnalytics();

  const isSelected = getCurrentNode(editor) === element;

  const toolbarItems = useMemo(
    (): BlockToolbarItem[] => [
      {
        icon: icons.Delete,
        title: 'Delete',
        onAction: () => removeVoidElement(editor, element),
        isVisible: !isReadOnly,
      },
    ],
    [element, editor, isReadOnly]
  );

  const toolbarVisible =
    (isSelected || hovering) && toolbarItems.some((item) => item.isVisible);

  const blockToolbar = (
    <BlockToolbar isVisible={toolbarVisible} items={toolbarItems} />
  );

  useEffect(() => {
    let pendingTimer: any = null;

    setError(false);

    if (!title || !loginUrl || !targetUrl || !clientId || !resourceLinkId) {
      setLoading(true);

      pendingTimer = setTimeout(() => {
        setLoading(false);
        setError(true);
      }, 1000);
    }
    return () => {
      if (pendingTimer) clearTimeout(pendingTimer);
    };
  }, [retry]);

  useEffect(() => {
    if (loginHint) {
      formRef.current?.requestSubmit();
    }
  }, [loginHint]);

  const context = useAulaEditorContext();
  const {
    entities,
    deleteEntity,
    config: { spaceId, userId, apiUrl, sessionToken },
  } = context;
  const { entity: entityId } = element;
  const selectedEntity = entities[entityId] as LtiEntity;

  if (selectedEntity?.type !== 'lti') {
    throw new Error('not allowed to render LtiBlockLink without lti entity');
  }

  // validate context
  if (!spaceId) {
    const message = 'Cannot display content. Space ID is unknown';
    // eslint-disable-next-line no-console
    console.error(message);
    return <p>{message}</p>;
  }

  const startLaunch = async () => {
    try {
      setLoading(true);
      track('LTI_BLOCK_LAUNCH', {
        itemType: context.config.itemType ?? 'Unknown',
        provider: title,
      });
      const ltiSessionToken = await lti1p3Launch({
        apiUrl,
        spaceId,
        clientId,
        type: LtiRequestType.RESOURCE_LINK,
        sessionToken,
        userId,
        resourceLinkId,
      });
      setLoginHint(ltiSessionToken);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const launchParams = selectedEntity.params;
  const isDeepLinking =
    launchParams?.launchType === LtiLaunchType.DEEP_LINKING &&
    !launchParams?.contentItem;

  const onCloseModal = () => {
    Transforms.delete(editor, {
      at: ReactEditor.findPath(editor, element),
      voids: true,
    });
    ReactEditor.focus(editor);
    deleteEntity(entityId);
  };

  if (isDeepLinking) {
    return (
      <DeepLinkingModal
        loginUrl={loginUrl}
        targetUrl={targetUrl}
        clientId={clientId}
        iss={iss}
        element={element}
        provider={title}
        onCloseModal={onCloseModal}
      />
    );
  }

  const targetLinkUri = launchParams?.contentItem?.url || targetUrl;

  return (
    <LtiBlockLinkWrapper
      isSelected={isSelected}
      isError={!!error}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      contentEditable={false}
    >
      {blockToolbar}
      {loading && (
        <Loader>
          <Loading />
        </Loader>
      )}
      {error && (
        <ErrorWrapper>
          <ErrorContainer>
            <icons.ErrorIcon />
            <ErrorTitle>There was an error loading {title}.</ErrorTitle>
          </ErrorContainer>
          <RetryContainer>
            <Button type="text" size="xsmall" onClick={() => setRetry(!retry)}>
              Retry
            </Button>
          </RetryContainer>
        </ErrorWrapper>
      )}
      {!error && !loading && (
        <ButtonWrapper
          onClick={startLaunch}
          data-testid="lti-button-wrapper"
          isSelected={isSelected}
        >
          <ImageWrapper>
            <img src={icon} alt="" height="24px" />
          </ImageWrapper>
          <TitleWrapper>{title}</TitleWrapper>
        </ButtonWrapper>
      )}
      <Lti1p3Form
        loginUrl={loginUrl}
        targetUrl={targetLinkUri}
        clientId={clientId}
        iss={iss}
        type={LtiRequestType.RESOURCE_LINK}
        target="_blank"
        loginHint={loginHint}
        formRef={formRef}
      />
    </LtiBlockLinkWrapper>
  );
};
